import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import Button from "../../components/Buttons/Button/Button";
import Buttons from "../../components/Buttons/Buttons/Buttons";
import { FormInput, FormSelect } from "../../components/Form/FormInputs/FormInput";
import FormWrapper from "../../components/Form/FormWrapper";
import MainCard from "../../components/layout/MainCard/MainCard";
import useAPI from "../../hooks/useAPI";
import { GlobalQueries } from "../../hooks/useRefreshQuery";
import Sitemap from "../../routes/sitemap";
import EmptyPage from "../Empty/EmptyPage";
import { currencyFormat } from "helpers/functions";

import CartTable from "./CartTable/CartTable";
import MobileCart from "./MobileCart/MobileCart";
import DeliveryOptionForm from "../Checkout/DeliveryOptionForm/DeliveryOptionForm";
import PromoCode from "../Checkout/PromoCode/PromoCode";
import { buildCheckoutOrderData } from "../Checkout/checkoutOrderData";
import useDelayedFlag from "../../hooks/useDelayedFlag";
import Loading from "../../components/Loading/Loading";

import scss from "./CartPage.module.scss";
import formScss from "../../components/Form/FormWrapper.module.scss";
import { useTranslation } from "react-i18next";
import { printError } from "../../helpers/apiError";

const CartPage = () => {
    const { t } = useTranslation();

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const api = useAPI();

    // Load available addresses
    const { data: addressData } = useQuery([GlobalQueries.Profile.Addresses], () => api.fetch(`/checkout/ddl/shipping_addresses`));
    const addresses = useMemo(() => addressData ?? [], [addressData]);

    // Load the current company
    const { data: customer } = useQuery([GlobalQueries.Profile.MyProfile, GlobalQueries.Profile.CompanyProfile], () => api.get(`/customer/verify`));

    // Selected addresses
    const [form, setForm] = useState({ shipping: null, billing: null, note: "", delivery_method: null, delivery_method_values: {}, payment_method: null });

    useEffect(
        () =>
            setForm((form) => ({
                ...form,
                shipping: form.shipping ?? (addresses.length > 0 ? addresses[0].id_company_address ?? null : null),
                billing: form.billing ?? (addresses.length > 0 ? addresses[0].id_company_address ?? null : null),
            })),
        [addresses]
    );

    // Get the shipping address
    const billingAddress = addresses.find((address) => address.type === "head_office");
    const shippingAddress = form.shipping ? addresses.find((address) => address.id_company_address === 1 * form.shipping) : null;

    // Base order data (no delivery_method_options yet - the delivery/payment option lists and the
    // delivery form don't need the sub-form values, and depending on them here would be circular
    // since those values only exist once the delivery options response is already known).
    const baseOrderData = useMemo(
        () => buildCheckoutOrderData({ form, billingAddress, shippingAddress }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [form, billingAddress, shippingAddress]
    );

    // note changes on every keystroke but never affects delivery/payment option eligibility or
    // promo code validity - keying these queries on the full baseOrderData would refetch all of
    // them (and the promo code section) on every keypress in the note field.
    const baseOrderDataQueryKey = useMemo(() => {
        const { note, ...rest } = baseOrderData;
        return rest;
    }, [baseOrderData]);

    // Load available delivery options - order data flows through so campaign conditions tied to
    // billing/shipping/already-selected methods are evaluated with the customer's live selections.
    const { data: deliveryOptionsData, isFetching: isFetchingDeliveryOptions } = useQuery(
        ["checkoutDeliveryOptions", baseOrderDataQueryKey],
        () => api.fetch(`/checkout/delivery-options`, { order_data: baseOrderData }),
        { keepPreviousData: true }
    );
    const deliveryOptions = useMemo(() => deliveryOptionsData?.options ?? [], [deliveryOptionsData]);

    // Load available payment options
    const { data: paymentOptionsData, isFetching: isFetchingPaymentOptions } = useQuery(
        ["checkoutPaymentOptions", baseOrderDataQueryKey],
        () => api.fetch(`/checkout/payment-options`, { order_data: baseOrderData }),
        { keepPreviousData: true }
    );
    const paymentOptions = useMemo(() => paymentOptionsData?.options ?? [], [paymentOptionsData]);

    // Only auto-select when there's exactly one option (and then the section is hidden below);
    // with 2+ options the customer must explicitly choose - no default selection.
    useEffect(
        () =>
            setForm((form) => ({
                ...form,
                delivery_method: form.delivery_method ?? (deliveryOptions.length === 1 ? deliveryOptions[0].id : null),
                payment_method: form.payment_method ?? (paymentOptions.length === 1 ? paymentOptions[0].id : null),
            })),
        [deliveryOptions, paymentOptions]
    );

    // The selected delivery method may expose its own dynamic form (e.g. store picker, courier details)
    const selectedDeliveryOption = deliveryOptions.find((option) => option.id === form.delivery_method);
    const { data: deliveryOptionFormData, isFetching: isFetchingDeliveryOptionForm } = useQuery(
        ["checkoutDeliveryOptionForm", form.delivery_method, baseOrderDataQueryKey],
        () => api.fetch(`/checkout/delivery-option-form/${form.delivery_method}`, { order_data: baseOrderData }),
        { enabled: !!selectedDeliveryOption?.provider, keepPreviousData: true }
    );
    const deliveryOptionFormFields = useMemo(
        () => (selectedDeliveryOption?.provider ? deliveryOptionFormData?.fields ?? [] : []),
        [selectedDeliveryOption?.provider, deliveryOptionFormData]
    );

    // display_shipping_address is a "switch" config field - backend leaves it as a raw "0"/"1" string,
    // not a boolean, so compare numerically. Hidden until a delivery method is actually selected.
    const showShippingAddress = selectedDeliveryOption ? Number(selectedDeliveryOption.display_shipping_address ?? 1) !== 0 : false;

    const selectedPaymentOption = paymentOptions.find((option) => option.id === form.payment_method);

    const [acceptMessage, setAcceptMessage] = useState(null);
    const acceptTerms = useRef("1");

    // Full order data, including the selected delivery method's sub-form values - sent on every
    // cart/summary fetch so campaign conditions (and totals/delivery fee) reflect what the
    // customer has actually selected so far, refreshing automatically whenever any of it changes.
    const orderData = useMemo(
        () => buildCheckoutOrderData({ form, billingAddress, shippingAddress, selectedDeliveryOption, deliveryOptionFormFields }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [form, billingAddress, shippingAddress, selectedDeliveryOption, deliveryOptionFormFields]
    );

    // Same note-exclusion as baseOrderDataQueryKey above, so typing in the note field doesn't
    // refetch the cart/summary either.
    const orderDataQueryKey = useMemo(() => {
        const { note, ...rest } = orderData;
        return rest;
    }, [orderData]);

    // Load all products in the cart + summary (totals, campaigns) - re-evaluated live as orderData changes.
    // keepPreviousData keeps showing the last known cart/summary while a new one is fetched in the
    // background, instead of the page dropping back to a blank/loading state on every form change.
    const { data, isLoading, isFetching: isFetchingCart } = useQuery([GlobalQueries.Cart, orderDataQueryKey], () => api.list(`/checkout`, { order_data: orderData }), {
        keepPreviousData: true,
    });

    const items = data?.items ?? [];
    const cost = data?.summary ?? null;

    // A background refresh is in flight (delivery/payment options, delivery form, or cart/summary).
    // Only surface a loader once it's been running longer than 400ms, so quick refreshes stay silent.
    const isRefreshing = isFetchingDeliveryOptions || isFetchingPaymentOptions || isFetchingDeliveryOptionForm || isFetchingCart;
    const showRefreshIndicator = useDelayedFlag(isRefreshing, 400);

    /**
     * Check data validation. Handling order save. Reload cart after save.
     */
    const [loading, setLoading] = useState(false);
    const submitHandler = async () => {
        setLoading(true);
        // Make sure we have billing address
        if (!billingAddress) {
            toast.warning(t("cart-default-text-select_address_for_invoice"));
            setLoading(false);
            return;
        }

        // Make sure we have shipping address
        if (!shippingAddress) {
            toast.warning(t("cart-default-text-select_shipping_address"));
            setLoading(false);
            return;
        }

        // Make sure a delivery method is selected
        if (!form.delivery_method) {
            toast.warning("Molimo izaberite način preuzimanja robe.");
            setLoading(false);
            return;
        }

        // Make sure a payment method is selected
        if (!form.payment_method) {
            toast.warning("Molimo izaberite način plaćanja.");
            setLoading(false);
            return;
        }

        // Make sure any required fields on the selected delivery method (e.g. store, courier details) are filled in
        const missingDeliveryField = deliveryOptionFormFields.find((field) => field.required && !form.delivery_method_values?.[field.prop_name]);
        if (missingDeliveryField) {
            toast.warning(missingDeliveryField.field_name);
            setLoading(false);
            return;
        }

        // Build the post data - billingAddress/shippingAddress/delivery_method/payment_method are
        // all guaranteed present at this point, so orderData is already fully populated (no null
        // fallbacks triggered); just add the promo code fields the live orderData doesn't carry.
        const data = {
            ...orderData,
            promo_code: null,
            promo_code_options: [],
        };

        api.post(`/checkout/one-page`, data)
            .then((response) => {
                if (response) {
                    toast.success(response?.message ? response?.message : t("cart-default-text-order_sent"));
                    queryClient.invalidateQueries(GlobalQueries.Checkout).catch();
                    queryClient.invalidateQueries(GlobalQueries.Orders).catch();
                    queryClient.invalidateQueries(GlobalQueries.Cart).catch();
                    navigate(Sitemap.cart.success + "/" + response?.order?.slug);
                    setLoading(false);
                } else {
                    setLoading(false);
                }
            })
            .catch((error) => {
                toast.error(printError(error) ? printError(error) : t("global-message-unknown_error"));
                setLoading(false);
            });
    };
    useEffect(() => {
        if (form.accept) {
            setAcceptMessage(false);
        }
    }, [form.accept]);

    // Show empty page
    if (!isLoading && items.length === 0) {
        return <EmptyPage message={"cart-default-text-no_content_in_cart"} translateKey={"cart-default"} />;
    }

    const freeDeliveryAmount = Number(cost?.options?.delivery?.free_delivery?.amount ?? 0);
    const deliveryProgress = freeDeliveryAmount > 0 ? Math.min(100, Math.max(0, Math.round((Number(cost?.totals?.subtotal ?? 0) / freeDeliveryAmount) * 100))) : 0;
    const summaryLabel = (key) => `${t(key).replace(/:\s*$/, "")}:`;

    return (
        <MainCard>
            <div className="page-container">
                <div className="page-content">
                    <h4 className={scss.pageTitle}>
                        {t("cart-default-title")}
                        {showRefreshIndicator && (
                            <span className={scss.refreshBadge}>
                                <Loading type="spin" size={16} className={scss.refreshSpinner} />
                                {t("cart-default-messages-refreshing")}
                            </span>
                        )}
                    </h4>
                    {cost && (
                        <div className={scss.wrapper}>
                            <div className={`${scss.cart} ${scss.section}`}>
                                <div className="mobile-hidden">
                                    <CartTable items={items} translateKey="cart-ordered_product" />
                                </div>
                                <div className="desktop-hidden">
                                    <MobileCart items={items} showImage={true} translateKey="cart-ordered_product" />
                                </div>
                            </div>
                            <FormWrapper className={`row ${scss.forms}`}>
                                <div className="col-xl-8">
                                    <section className={scss.summary} aria-label={t("cart-total_for_payment-title")}>
                                        <div className={scss.summaryRow}><span>{summaryLabel("cart-total_for_payment-table_th-amount_without_rebate")}</span><span>{currencyFormat(cost?.totals?.with_out_vat, cost?.currency)}</span></div>
                                        <div className={scss.summaryRow}><span>{summaryLabel("cart-total_for_payment-table_th-rebat_amount")}</span><span>{Number(cost?.totals?.rebate_amount) > 0 ? `-${currencyFormat(cost.totals.rebate_amount, cost.currency)}` : currencyFormat(0, cost?.currency)}</span></div>
                                        <div className={scss.summaryRow}><span>{summaryLabel("cart-total_for_payment-table_th-discount")}</span><span>{Number(cost?.totals?.items_discount_amount) + Number(cost?.totals?.cart_discount_amount) > 0 ? `-${currencyFormat(Number(cost?.totals?.items_discount_amount) + Number(cost?.totals?.cart_discount_amount), cost?.currency)}` : currencyFormat(0, cost?.currency)}</span></div>
                                        {cost?.totals?.promo_code_amount > 0 && <div className={scss.summaryRow}><span>{summaryLabel("cart-default-total_for_payment-table_th-promo_code")}</span><span>-{currencyFormat(cost?.totals?.promo_code_amount, cost?.currency)}</span></div>}
                                        <div className={scss.summaryRow}><span>{summaryLabel("cart-total_for_payment-table_th-total")}</span><span>{currencyFormat(cost?.totals?.subtotal, cost?.currency)}</span></div>
                                        {Number(cost?.totals?.vat ?? 0) !== 0 && <div className={scss.summaryRow}><span>{summaryLabel("cart-total_for_payment-table_th-vat")}</span><span>{currencyFormat(cost?.totals?.vat, cost?.currency)}</span></div>}
                                        <div className={scss.summaryRow}><span>{summaryLabel("cart-total_for_payment-table_th-delivery")}</span><span>{currencyFormat(cost?.totals?.delivery_amount, cost?.currency)}</span></div>
                                        <div className={scss.summaryTotal}><span>{summaryLabel("cart-total_for_payment-title")}</span><strong>{currencyFormat(cost.totals.total, cost.currency)}</strong></div>
                                    </section>

                                    {freeDeliveryAmount > 0 && (
                                        <div className={scss.freeDelivery}>
                                            <div className={scss.deliveryProgress} role="progressbar" aria-label={t("cart_checkout-default-free_shipping_info-delivery")} aria-valuenow={deliveryProgress} aria-valuemin={0} aria-valuemax={100}>
                                                <span style={{ width: `${deliveryProgress}%` }} />
                                            </div>
                                            <p>
                                                {deliveryProgress < 100 ? (
                                                    <>{t("cart_checkout-default-text-before_free_shipping")} <strong>{currencyFormat(Math.max(0, freeDeliveryAmount - Number(cost?.totals?.subtotal ?? 0)), cost?.currency)}</strong>.</>
                                                ) : (
                                                    <>{t("cart_checkout-default-free_shipping_info-you_have_achieved")} {t("cart_checkout-default-free_shipping_info-free")} {t("cart_checkout-default-free_shipping_info-delivery")}.</>
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="col-xl-4">
                                    {/* Customer info */}
                                    <h6 className={`underline bold ${scss.title}`}>{t("cart-customer_info-title")}:</h6>
                                    <div className={`pl ${scss.formSection}`}>
                                        <p className="m-0">
                                            {t("cart-customer_info-p1")} : <span className="text-bold">{customer?.company?.name}</span>
                                        </p>
                                        <p className="m-0">
                                            {t("cart-customer_info-p2")}{" "}
                                            <Link to={Sitemap.profile.company_profile} className="text-bold">
                                                {t("cart-customer_info-span2")}
                                            </Link>
                                            .
                                        </p>
                                    </div>

                                    {/* Delivery method picker - only shown when there's an actual choice to make (2+ options) */}
                                    {deliveryOptions.length > 1 && (
                                        <>
                                            <h6 className={`underline bold ${scss.title}`}>Način preuzimanja robe:</h6>
                                            <div className={`pl ${scss.formSection}`}>
                                                <FormSelect
                                                    field="delivery_method"
                                                    data={form}
                                                    setData={(updater) =>
                                                        setForm((prev) => {
                                                            const updated = typeof updater === "function" ? updater(prev) : updater;
                                                            return { ...updated, delivery_method_values: {} };
                                                        })
                                                    }
                                                    options={[{ id: "", name: "-" }, ...deliveryOptions]}
                                                />
                                                {selectedDeliveryOption?.checkout_message && <p className="m-0">{selectedDeliveryOption.checkout_message}</p>}
                                            </div>
                                        </>
                                    )}

                                    {/*
                                        Extra fields for whichever delivery method is currently selected (store
                                        picker, courier details, ...) - its own section, always shown regardless
                                        of the picker above, since it's required input even when there's only one
                                        delivery method to auto-select.
                                    */}
                                    {deliveryOptionFormFields.length > 0 && (
                                        <>
                                            <h6 className={`underline bold ${scss.title}`}>{selectedDeliveryOption?.name}:</h6>
                                            <div className={`pl ${scss.formSection}`}>
                                                <DeliveryOptionForm
                                                    fields={deliveryOptionFormFields}
                                                    values={form.delivery_method_values}
                                                    setValues={(updater) =>
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            delivery_method_values: typeof updater === "function" ? updater(prev.delivery_method_values ?? {}) : updater,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        </>
                                    )}

                                    {/* Shipment */}
                                    {showShippingAddress && (
                                        <>
                                            <h6 className={`underline bold ${scss.title}`}>{t("cart-shipment-title")}:</h6>
                                            <div className={`pl ${scss.formSection}`}>
                                                <FormSelect field="shipping" data={form} setData={setForm} options={addresses} />
                                            </div>
                                        </>
                                    )}

                                    {/* Payment method */}
                                    {paymentOptions.length > 1 && (
                                        <>
                                            <h6 className={`underline bold ${scss.title}`}>Način plaćanja:</h6>
                                            <div className={`pl ${scss.formSection}`}>
                                                <FormSelect field="payment_method" data={form} setData={setForm} options={[{ id: "", name: "-" }, ...paymentOptions]} />
                                                {selectedPaymentOption?.checkout_message && <p className="m-0">{selectedPaymentOption.checkout_message}</p>}
                                            </div>
                                        </>
                                    )}

                                    <PromoCode baseOrderData={baseOrderDataQueryKey} refreshQueryKey={[GlobalQueries.Cart]} />

                                    <h6 className={`underline bold ${scss.title}`}>{t("cart-note-title")}:</h6>
                                    <div className={`${formScss.input} pl ${scss.formSection}`}>
                                        <FormInput field="note" data={form} placeholder="" setData={setForm} type="textarea" rows="5" />
                                        <div className="row align-items-center mobile-hidden" ref={acceptTerms}>
                                            {acceptMessage && <span className="red-text">{acceptMessage}</span>}
                                        </div>
                                    </div>
                                </div>
                            </FormWrapper>
                            <div className="row align-items-center desktop-hidden" ref={acceptTerms}>
                                {acceptMessage && <span className="red-text">{acceptMessage}</span>}
                            </div>
                            <Buttons align="center" className="col-12">
                                <Button label={t("cart-default-button-continue")} onClick={() => navigate("/pocetna")} className={scss.continue} />
                                <Button loading={loading} label={t("cart-default-button-finish")} onClick={() => submitHandler()} className={scss.checkout} />
                            </Buttons>
                        </div>
                    )}
                </div>
            </div>
        </MainCard>
    );
};

export default CartPage;
